"use client"

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { User, getUser, setUser, clearAuth, removeToken, setGlobalLogoutCallback } from './utils'

interface AuthContextType {
  user: User | null
  setAuthUser: (user: User) => void
  logout: () => void
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Load user from localStorage on initial render
  useEffect(() => {
    const storedUser = getUser()
    setUserState(storedUser)
    setIsLoading(false)
  }, [])

  // Register logout callback for 401 handling
  useEffect(() => {
    setGlobalLogoutCallback(() => {
      setUserState(null)
    })
    return () => {
      setGlobalLogoutCallback(null)
    }
  }, [])

  // Set user and store in localStorage
  const setAuthUser = (userData: User) => {
    setUser(userData)
    setUserState(userData)
  }

  // Logout and clear auth data
  const logout = () => {
    clearAuth()
    removeToken()
    // 清理登录时写入的所有散 key
    if (typeof window !== 'undefined') {
      localStorage.removeItem('username')
      localStorage.removeItem('nickname')
      localStorage.removeItem('talkId')
      localStorage.removeItem('uid')
      localStorage.removeItem('isGuest')
      localStorage.removeItem('token')
    }
    setUserState(null)
  }

  const value = {
    user,
    setAuthUser,
    logout,
    isLoading
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
